"use client"

import { useState, useEffect, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import Navbar from "../../components/account/Navbar"
import { useNotifications } from "../../hooks/useNotifications"
import { useTranslation } from "react-i18next"
import "./accountmanage.css"

// Import icons
import BellIcon from "/icons/account/bell.svg"
import TagIcon from "/icons/account/tag.svg"
import NetworkOnlineIcon from "/icons/account/network-online.svg"
import NewPostIcon from "/icons/account/new-post.svg"
import PostInteractionsIcon from "/icons/account/post-interactions.svg"
import FollowingInvitationIcon from "/icons/account/following-invitation.svg"
import FollowRequestAcceptedIcon from "/icons/account/follow-request-accepted.svg"
import ChatIcon from "/icons/account/chat.svg"
import CallIcon from "/icons/account/call.svg"



// Notification Settings Content Component (for use within AccountPreferences)
const NotificationSettingsContent = () => {
  const { isPermissionGranted, requestPermission, isLoading: isRequestingPermission } = useNotifications()
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir() === "rtl"

  const generalNotificationItems = useMemo(
    () => [
      {
        key: "receiveAppNotifications",
        icon: BellIcon,
        altKey: "notificationSettings.icons.bell",
        titleKey: "notificationSettings.items.receiveApp.title",
        descriptionKey: "notificationSettings.items.receiveApp.description",
      },
      {
        key: "receiveOfferNotifications",
        icon: TagIcon,
        altKey: "notificationSettings.icons.tag",
        titleKey: "notificationSettings.items.receiveOffers.title",
        descriptionKey: "notificationSettings.items.receiveOffers.description",
      },
    ],
    [t]
  )

  const connectNotificationItems = useMemo(
    () => [
      {
        key: "networkOnlineNotifications",
        icon: NetworkOnlineIcon,
        altKey: "notificationSettings.icons.network",
        titleKey: "notificationSettings.items.networkOnline.title",
        descriptionKey: "notificationSettings.items.networkOnline.description",
      },
      {
        key: "newUserSameCityNotifications",
        icon: NetworkOnlineIcon,
        altKey: "notificationSettings.icons.network",
        titleKey: "notificationSettings.items.newUserSameCity.title",
        descriptionKey: "notificationSettings.items.newUserSameCity.description",
      },
      {
        key: "newPostNotifications",
        icon: NewPostIcon,
        altKey: "notificationSettings.icons.newPost",
        titleKey: "notificationSettings.items.newPost.title",
        descriptionKey: "notificationSettings.items.newPost.description",
      },
      {
        key: "postInteractionNotifications",
        icon: PostInteractionsIcon,
        altKey: "notificationSettings.icons.postInteractions",
        titleKey: "notificationSettings.items.postInteractions.title",
        descriptionKey: "notificationSettings.items.postInteractions.description",
      },
      {
        key: "followingInvitationNotifications",
        icon: FollowingInvitationIcon,
        altKey: "notificationSettings.icons.followInvitation",
        titleKey: "notificationSettings.items.followInvitation.title",
        descriptionKey: "notificationSettings.items.followInvitation.description",
      },
      {
        key: "followRequestAcceptedNotifications",
        icon: FollowRequestAcceptedIcon,
        altKey: "notificationSettings.icons.followAccepted",
        titleKey: "notificationSettings.items.followAccepted.title",
        descriptionKey: "notificationSettings.items.followAccepted.description",
      },
      {
        key: "chatNotifications",
        icon: ChatIcon,
        altKey: "notificationSettings.icons.chat",
        titleKey: "notificationSettings.items.chat.title",
        descriptionKey: "notificationSettings.items.chat.description",
      },
      {
        key: "callNotifications",
        icon: CallIcon,
        altKey: "notificationSettings.icons.call",
        titleKey: "notificationSettings.items.call.title",
        descriptionKey: "notificationSettings.items.call.description",
      },
    ],
    [t]
  )

  const renderNotificationItem = (item) => {
    // Check if this is the new user same city notification and if user has no city
    const isNewUserSameCity = item.key === "newUserSameCityNotifications"
    const hasCity = userData?.city || userData?.city_data?.id
    const shouldShow = !isNewUserSameCity || hasCity

    if (!shouldShow) {
      return null
    }

    return (
      <div className="notificationsettings-notification-item" key={item.key}>
        <div className="notificationsettings-notification-content">
          <div className="notificationsettings-notification-icon">
            <img
              src={item.icon || "/placeholder.svg"}
              alt={t(item.altKey)}
              width={20}
              height={20}
            />
          </div>
          <div>
            <div className="notificationsettings-notification-text">
              {t(item.titleKey)}
            </div>
            <div className="notificationsettings-notification-description">
              {t(item.descriptionKey)}
            </div>
          </div>
        </div>

        <div className="notificationsettings-toggle-container">
          {successMessages[item.key] && (
            <div className="notificationsettings-success-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>
              {t("notificationSettings.status.saved")}
            </div>
          )}
          <button
            onClick={() => handleNotificationToggle(item.key)}
            className={`notificationsettings-toggle-switch ${notificationSettings[item.key] ? "active" : ""}`}
            disabled={isNewUserSameCity && !hasCity}
          >
            <div className={`notificationsettings-toggle-knob ${notificationSettings[item.key] ? "active" : ""}`} />
          </button>
        </div>
      </div>
    )
  }

  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    // General Notifications
    receiveAppNotifications: true,
    receiveOfferNotifications: true,

    // Social Media Notifications
    networkOnlineNotifications: true,
    newUserSameCityNotifications: false,
    newPostNotifications: true,
    postInteractionNotifications: true,
    followingInvitationNotifications: true,
    followRequestAcceptedNotifications: true,
    chatNotifications: true,
    callNotifications: true,
    sosRequestPreference: 'no_none',
  })
  const [isLoading, setIsLoading] = useState(true)
  const [successMessages, setSuccessMessages] = useState({})
  const [userData, setUserData] = useState(null)

  // Load notification settings and user data from API on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem('access_token')
        if (!token) {
          console.error('No access token found')
          setIsLoading(false)
          return
        }

        const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

        // Load notification settings
        const settingsResponse = await fetch(`${API_BASE_URL}/api/v1/notifications-settings`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        })

        // Load user profile to check city
        const userResponse = await fetch(`${API_BASE_URL}/api/v1/user-profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        })

        if (settingsResponse.ok) {
          var settingsData = await settingsResponse.json()
          settingsData = settingsData.data;
          setNotificationSettings({
            receiveAppNotifications: settingsData.receive_app_notifications ?? false,
            receiveOfferNotifications: settingsData.get_updates_special_offers ?? false,
            networkOnlineNotifications: settingsData.notify_when_connection_online ?? false,
            newUserSameCityNotifications: settingsData.notify_new_user_same_city ?? false,
            newPostNotifications: settingsData.notify_new_post_added ?? false,
            postInteractionNotifications: settingsData.notify_user_likes_comments_shares ?? false,
            followingInvitationNotifications: settingsData.notify_new_connection_requests ?? false,
            followRequestAcceptedNotifications: settingsData.notify_accepted_connection ?? false,
            chatNotifications: settingsData.notify_receive_chat ?? false,
            callNotifications: settingsData.notify_incoming_calls ?? false,
            sosRequestPreference: settingsData.sos_request_preference ?? 'no_none',
          })
        } else {
          console.error('Failed to load notification settings:', settingsResponse.status)
        }

        if (userResponse.ok) {
          var userDataResponse = await userResponse.json()
          const userDataResult = userDataResponse.data || userDataResponse.user || userDataResponse
          setUserData(userDataResult)
        } else {
          console.error('Failed to load user data:', userResponse.status)
        }
      } catch (error) {
        console.error('Error loading data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [])


  const handleNotificationToggle = async (settingKey) => {
    // Map the setting key to the API field name
    const apiFieldMapping = {
      receiveAppNotifications: 'receive_app_notifications',
      receiveOfferNotifications: 'get_updates_special_offers',
      networkOnlineNotifications: 'notify_when_connection_online',
      newUserSameCityNotifications: 'notify_new_user_same_city',
      newPostNotifications: 'notify_new_post_added',
      postInteractionNotifications: 'notify_user_likes_comments_shares',
      followingInvitationNotifications: 'notify_new_connection_requests',
      followRequestAcceptedNotifications: 'notify_accepted_connection',
      chatNotifications: 'notify_receive_chat',
      callNotifications: 'notify_incoming_calls'
    }

    const apiField = apiFieldMapping[settingKey]
    if (!apiField) {
      console.error('Unknown setting key:', settingKey)
      return
    }

    // Get the new value (opposite of current)
    const newValue = !notificationSettings[settingKey]

    // Optimistically update the UI
    setNotificationSettings((prev) => ({
      ...prev,
      [settingKey]: newValue,
    }))

    try {
      // Get the current token
      const token = localStorage.getItem('access_token')
      if (!token) {
        console.error('No access token found')
        return
      }

      // Prepare the payload with all current settings
      const payload = {
        receive_app_notifications: notificationSettings.receiveAppNotifications,
        get_updates_special_offers: notificationSettings.receiveOfferNotifications,
        notify_when_connection_online: notificationSettings.networkOnlineNotifications,
        notify_new_user_same_city: notificationSettings.newUserSameCityNotifications,
        notify_new_post_added: notificationSettings.newPostNotifications,
        notify_user_likes_comments_shares: notificationSettings.postInteractionNotifications,
        notify_new_connection_requests: notificationSettings.followingInvitationNotifications,
        notify_accepted_connection: notificationSettings.followRequestAcceptedNotifications,
        notify_receive_chat: notificationSettings.chatNotifications,
        notify_incoming_calls: notificationSettings.callNotifications,
        sos_request_preference: notificationSettings.sosRequestPreference
      }

      // Update the specific field that was toggled
      payload[apiField] = newValue

      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

      const response = await fetch(`${API_BASE_URL}/api/v1/notifications-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`)
      }

      const result = await response.json()

      // Show success message for this specific setting
      setSuccessMessages(prev => ({
        ...prev,
        [settingKey]: true
      }))

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessages(prev => ({
          ...prev,
          [settingKey]: false
        }))
      }, 3000)

    } catch (error) {
      console.error('Failed to update notification settings:', error)

      // Revert the optimistic update on error
      setNotificationSettings((prev) => ({
        ...prev,
        [settingKey]: !newValue,
      }))

      // You could show an error message to the user here
      alert(t("notificationSettings.errors.updateFailed"))
    }
  }

  const handleSOSPreferenceChange = async (newValue) => {
    // Optimistically update
    setNotificationSettings(prev => ({
      ...prev,
      sosRequestPreference: newValue
    }));

    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const payload = {
        receive_app_notifications: notificationSettings.receiveAppNotifications,
        get_updates_special_offers: notificationSettings.receiveOfferNotifications,
        notify_when_connection_online: notificationSettings.networkOnlineNotifications,
        notify_new_user_same_city: notificationSettings.newUserSameCityNotifications,
        notify_new_post_added: notificationSettings.newPostNotifications,
        notify_user_likes_comments_shares: notificationSettings.postInteractionNotifications,
        notify_new_connection_requests: notificationSettings.followingInvitationNotifications,
        notify_accepted_connection: notificationSettings.followRequestAcceptedNotifications,
        notify_receive_chat: notificationSettings.chatNotifications,
        notify_incoming_calls: notificationSettings.callNotifications,
        sos_request_preference: newValue
      };

      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

      const response = await fetch(`${API_BASE_URL}/api/v1/notifications-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to update SOS preference');

      setSuccessMessages(prev => ({ ...prev, sosRequestPreference: true }));
      setTimeout(() => {
        setSuccessMessages(prev => ({ ...prev, sosRequestPreference: false }));
      }, 3000);

    } catch (error) {
      console.error('Error updating SOS preference:', error);
      // Revert on error
      setNotificationSettings(prev => ({
        ...prev,
        sosRequestPreference: notificationSettings.sosRequestPreference
      }));
      alert(t("notificationSettings.errors.updateFailed"));
    }
  }

  return (
    <div
      className={`notificationsettings-container ${isRTL ? "rtl" : ""}`}
      dir={i18n.dir()}
    >
      <main className="notificationsettings-main">
        {/* Loading State */}
        {isLoading && (
          <div className="notificationsettings-loading">
            <div className="notificationsettings-loading-spinner" />
            <p className="notificationsettings-loading-text">
              {t("notificationSettings.loading")}
            </p>
          </div>
        )}

        {/* General Notifications Section */}
        {!isLoading && (
          <div className="notificationsettings-section">
            <h3 className="notificationsettings-section-title">
              {t("notificationSettings.sections.general")}
            </h3>

            {generalNotificationItems.map(renderNotificationItem)}
          </div>
        )}

        {/* Separator Line */}
        {!isLoading && <div className="notificationsettings-separator"></div>}

        {/* Sulhufa Connect Notifications Section */}
        {!isLoading && (
          <div className="notificationsettings-section-large">
            <h3 className="notificationsettings-section-title">
              {t("notificationSettings.sections.connect")}
            </h3>

            {connectNotificationItems.map(renderNotificationItem)}
          </div>
        )}

        {/* SOS Request Settings (Veterinarians Only) */}
        {!isLoading && userData?.user_type === 'VETERINARIAN' && (
          <div className="notificationsettings-section">
            <h3 className="notificationsettings-section-title">
              {t("notificationSettings.sections.sos") || "SOS Request Settings"}
            </h3>

            <div className="notificationsettings-notification-item">
              <div className="notificationsettings-notification-content">
                <div className="notificationsettings-notification-icon">
                  <img
                    src={BellIcon} // Using BellIcon as a fallback/placeholder
                    alt="SOS"
                    width={20}
                    height={20}
                  />
                </div>
                <div>
                  <div className="notificationsettings-notification-text">
                    {t("notificationSettings.items.sosPreference.title") || "Receive SOS Requests"}
                  </div>
                  <div className="notificationsettings-notification-description">
                    {t("notificationSettings.items.sosPreference.description") || "Choose which SOS requests you want to receive"}
                  </div>
                </div>
              </div>

              <div className="notificationsettings-toggle-container">
                {successMessages['sosRequestPreference'] && (
                  <div className="notificationsettings-success-message">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                    {t("notificationSettings.status.saved")}
                  </div>
                )}
                <select
                  className="notificationsettings-select"
                  value={notificationSettings.sosRequestPreference}
                  onChange={(e) => handleSOSPreferenceChange(e.target.value)}
                  style={{
                    padding: '8px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    backgroundColor: 'white',
                    fontSize: '14px',
                    color: '#1e293b'
                  }}
                >
                  <option value="all">{t("notificationSettings.items.sosPreference.options.all")}</option>
                  <option value="nearby">{t("notificationSettings.items.sosPreference.options.nearby")}</option>
                  <option value="city">{t("notificationSettings.items.sosPreference.options.city")}</option>
                  <option value="no_none">{t("notificationSettings.items.sosPreference.options.no_none")}</option>
                </select>
              </div>
            </div>
            <div className="notificationsettings-separator"></div>
          </div>
        )}

      </main>
    </div>
  )
}

// Wrapper component for backward compatibility
const NotificationSettings = () => {
  const navigate = useNavigate()
  const { userType } = useParams()

  return (
    <div className="notificationsettings-page">
      <Navbar userType={userType} />
      <NotificationSettingsContent />
    </div>
  )
}

export default NotificationSettings
export { NotificationSettingsContent }
